import { Decimal } from "decimal.js";

import { Prisma } from "@/.zenstack/logical-prisma-client";

const _auctionDetailsArgs = Prisma.validator<Prisma.AuctionFormatDefaultArgs>()(
  {
    select: {
      bidIncrement: true,
      startingBid: true,
      reserveBidAmount: true,
      auctionStartDate: true,
      auctionEndDate: true,
    },
  },
);

export type AuctionDetails = Prisma.AuctionFormatGetPayload<
  typeof _auctionDetailsArgs
>;

const _bidArgs = Prisma.validator<Prisma.BidDefaultArgs>()({
  select: {
    id: true,
    createdAt: true,
    createdById: true,
    placedBy: true,
    propertySeekerId: true,
    currentBidAmount: true,
    maxBidAmount: true,
  },
});

export type BidDetails = Prisma.BidGetPayload<typeof _bidArgs>;

export function getAuctionBiddingDetails(
  auction: AuctionDetails,
  bids: BidDetails[] = [],
) {
  const biddingDetails = getBiddingDetails(bids);

  // Helper function to determine if a bid is actually winning (meets reserve price)
  const isBidWinning = (bid: BidDetails): boolean => {
    if (!auction.reserveBidAmount) {
      // No reserve price set, so highest bid wins
      return biddingDetails.highestPlacedBid?.id === bid.id;
    }

    const bidAmount = bid.currentBidAmount
      ? new Decimal(bid.currentBidAmount.toString())
      : new Decimal(0);
    const reserveAmount = new Decimal(auction.reserveBidAmount.toString());

    // Bid must be the highest AND meet or exceed the reserve price
    return (
      biddingDetails.highestPlacedBid?.id === bid.id &&
      bidAmount.greaterThanOrEqualTo(reserveAmount)
    );
  };

  // Convenience method to get the winning bid (if any)
  const getWinningBid = (): BidDetails | null => {
    return bids.find((bid) => isBidWinning(bid)) || null;
  };

  const minimumNextBidAmount = Decimal.max(
    new Decimal(auction.bidIncrement.toString()).plus(
      biddingDetails.highestPlacedBid?.currentBidAmount
        ? new Decimal(
            biddingDetails.highestPlacedBid.currentBidAmount.toString(),
          )
        : new Decimal(0),
    ),
    new Decimal(auction.startingBid.toString()),
  );

  // const minimumNextMaxBidAmount = minimumNextBidAmount.add(
  //   auction.bidIncrement,
  // );

  const currentBidAmount = Decimal.max(
    biddingDetails.highestPlacedBid?.currentBidAmount
      ? new Decimal(biddingDetails.highestPlacedBid.currentBidAmount.toString())
      : new Decimal(0),
    new Decimal(auction.startingBid.toString()),
  );

  // These bids will be
  // - filtered by (currentBidAmount or maxBidAmount) >= nextBidAmount
  // - sorted by maxBidAmount DESC, filtering (winner in the first position)
  // - unique by propertySeekerId
  const autoBiddingBids = bids
    .filter((b) =>
      Decimal.max(
        b.currentBidAmount
          ? new Decimal(b.currentBidAmount.toString())
          : new Decimal(0),
        b.maxBidAmount
          ? new Decimal(b.maxBidAmount.toString())
          : new Decimal(0),
      ).greaterThanOrEqualTo(minimumNextBidAmount),
    )
    .sort((a, b) => Number(b.maxBidAmount) - Number(a.maxBidAmount))
    .reduce(
      (accumulator, bid) => {
        // If we haven't seen this propertySeekerId yet, add it
        if (
          !accumulator.some(
            (existingBid) =>
              existingBid.propertySeekerId === bid.propertySeekerId,
          )
        ) {
          accumulator.push(bid);
        }
        return accumulator;
      },
      [] as typeof bids,
    );
  // .map(
  //   // ensure every bid has a unique propertySeekerId, throwing an error if not
  //   (bid, index) => {
  //     const duplicateIndex = bids.findIndex(
  //       (otherBid, otherIndex) =>
  //         otherIndex !== index &&
  //         otherBid.propertySeekerId === bid.propertySeekerId,
  //     );
  //     if (duplicateIndex !== -1) {
  //       throw new Error(
  //         `Auto-bidding bids must have a unique propertySeekerId. Found duplicate propertySeekerId '${bid.propertySeekerId}' at indices ${index} and ${duplicateIndex}`,
  //       );
  //     }
  //     return bid;
  //   },
  // )
  // .map(
  //   // ensure every bid has the same currentBidAmount, throwing an error if not
  //   (bid, _index) => {
  //     if (bid.currentBidAmount === bids[0]?.currentBidAmount) {
  //       throw new Error(
  //         "Auto-bidding bids must have the same currentBidAmount",
  //       );
  //     }
  //     return bid;
  //   },
  // )
  // .map((bid) => {
  //   console.log(bid);
  //   return bid;
  // });

  const highestPlacedLosingBidsGroupedByUser = bids
    .filter((bid) => {
      return !isBidWinning(bid);
    })
    .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime()) // Sort by time DESC (most recent first)
    .reduce(
      (accumulator, bid) => {
        // If we haven't seen this propertySeekerId yet, add it
        if (
          !accumulator.some(
            (existingBid) =>
              existingBid.propertySeekerId === bid.propertySeekerId,
          )
        ) {
          accumulator.push(bid);
        }
        return accumulator;
      },
      [] as typeof bids,
    );

  const isAuctionEnded = auction.auctionEndDate < new Date();
  const isAuctionStarted = auction.auctionStartDate < new Date();
  const isAuctionUpcoming = auction.auctionStartDate > new Date();

  const minimumNextWinningBidAmount = Math.max(
    biddingDetails.highestPlacedMaxBid?.maxBidAmount
      ? new Decimal(biddingDetails.highestPlacedMaxBid.maxBidAmount.toString())
          .plus(new Decimal(auction.bidIncrement.toString()))
          .toNumber()
      : 0,
    new Decimal(auction.startingBid.toString()).toNumber(),
  );

  return {
    ...auction,

    /* Auction end date is in the past */
    isAuctionEnded,

    /* Auction start date is in the future */
    isAuctionUpcoming,

    /* Auction start date is in the past and end date is in the future */
    isAuctionActive: isAuctionStarted && !isAuctionEnded,

    ...biddingDetails,

    /* Current bid amount */
    currentBidAmount,

    /* Minimum next bid amount */
    minimumNextBidAmount,

    /* Minimum next max bid amount */
    // minimumNextMaxBidAmount,

    /* Minimum next winning bid amount, not considering the reserve price */
    minimumNextWinningBidAmount,

    /* All qualified auto-bidding bids for the current round */
    autoBiddingBids,

    /* Highest losing bids grouped by user */
    highestPlacedLosingBidsGroupedByUser,

    /* Convenience method to get the winning bid (if any) */
    getWinningBid,
  };
}

// // CHORE: Local workaround: safely compare Decimal-like values that may be strings/numbers on the client
// function compareAsNumber(
//   a: unknown,
//   b: unknown,
//   op: "gt" | "lt" | "gte" | "lte",
// ): boolean {
//   const na = Number(a ?? 0);
//   const nb = Number(b ?? 0);
//   return op === "gt"
//     ? na > nb
//     : op === "lt"
//       ? na < nb
//       : op === "gte"
//         ? na >= nb
//         : na <= nb; // lte
// }

export function getBiddingDetails(bids: BidDetails[] = []) {
  const bidCount = bids.length;

  const highestPlacedBid =
    bids.length > 0
      ? bids
          .filter(
            (bid) =>
              Number(bid.currentBidAmount) ===
              Math.max(...bids.map((b) => Number(b.currentBidAmount))),
          )
          .sort((a, b) => {
            // First, sort by createdAt (newest first) - this is the primary tiebreaker
            // when currentBidAmounts are equal (e.g., when a winning bid is lowered)
            const timeA = new Date(a.createdAt).getTime();
            const timeB = new Date(b.createdAt).getTime();
            if (timeB !== timeA) {
              return timeB - timeA; // Descending: newest first
            }
            // If timestamps are equal, use maxBidAmount as secondary tiebreaker
            return Number(b.maxBidAmount) - Number(a.maxBidAmount); // Descending: highest maxBid first
          })[0] || null
      : null;

  const lowestPlacedBid =
    bids.length > 0
      ? bids
          .filter(
            (bid) =>
              Number(bid.currentBidAmount) ===
              Math.min(...bids.map((b) => Number(b.currentBidAmount))),
          )
          .reduce((min, bid) =>
            Number(bid.currentBidAmount) < Number(min?.currentBidAmount)
              ? bid
              : min,
          )
      : null;

  const highestPlacedMaxBid =
    bids.length > 0
      ? bids.reduce(
          (max, bid) => {
            return new Decimal(bid.maxBidAmount.toString()).greaterThan(
              new Decimal(max?.maxBidAmount.toString() ?? "0"),
            )
              ? bid
              : max;
          },
          null as (typeof bids)[0] | null,
        )
      : null;

  return {
    isWinning: (userId: string | undefined) =>
      highestPlacedBid?.propertySeekerId === userId,

    bidById: (bidId: string) => bids.find((b) => b.id === bidId),

    bidsByUserId: (userId: string) =>
      bids
        .filter((b) => b.propertySeekerId === userId)
        .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()),

    topBidByUserId: (userId: string) =>
      bids
        .filter((b) => b.propertySeekerId === userId)
        .reduce(
          (max, bid) =>
            Number(bid.maxBidAmount) > Number(max?.maxBidAmount) ? bid : max,
          null as (typeof bids)[0] | null,
        ),

    bidsGroupedByUserId: (
      userId?: string, // used for bidding history, to hide updates to Max Bid Amounts of other users.
    ) => getBidsGroupedByUserId(bids, userId),

    bidPosition: (userId?: string) =>
      getBidsGroupedByUserId(bids, userId).findIndex(
        (b: Pick<BidDetails, "propertySeekerId">) =>
          b.propertySeekerId === userId,
      ) + 1,

    // What's the total count of bids?
    bidCount,

    // How much has the bidding increased?
    bidIncrease: highestPlacedBid?.currentBidAmount
      ? new Decimal(highestPlacedBid.currentBidAmount.toString()).minus(
          lowestPlacedBid?.currentBidAmount
            ? new Decimal(lowestPlacedBid.currentBidAmount.toString())
            : new Decimal(0),
        )
      : new Decimal(0),

    // What is the highest bid?
    highestPlacedBid,

    // What is the lowest bid?
    lowestPlacedBid,

    // What is the highest max bid? (We don't want to reveal this to users...)
    highestPlacedMaxBid,
  };
}

function getBidsGroupedByUserId(
  bids: BidDetails[],
  userId?: string,
): (BidDetails & {
  addedMaxBidAmount: boolean;
  updatedMaxBidAmount: boolean;
})[] {
  // Step 1: Build Map<propertySeekerId, BidDetails[]>
  const bidsByUser = new Map<string, BidDetails[]>();
  bids.forEach((bid) => {
    const existing = bidsByUser.get(bid.propertySeekerId) || [];
    existing.push(bid);
    bidsByUser.set(bid.propertySeekerId, existing);
  });

  const result: BidDetails[] = [];

  // Step 2: If userId provided, add all their bids to result and remove from map
  if (userId && bidsByUser.has(userId)) {
    result.push(...bidsByUser.get(userId)!);
    bidsByUser.delete(userId);
  }

  // Step 3: For remaining users, pick the bid with highest maxBidAmount at each currentBidAmount,
  // or the only bid if there's just one
  bidsByUser.forEach((userBids) => {
    // Group by currentBidAmount for this user
    const bidsByAmount = new Map<string, BidDetails[]>();
    userBids.forEach((bid) => {
      const amountKey = bid.currentBidAmount.toString();
      const existing = bidsByAmount.get(amountKey) || [];
      existing.push(bid);
      bidsByAmount.set(amountKey, existing);
    });

    // For each amount group, pick the bid with highest maxBidAmount
    bidsByAmount.forEach((amountBids) => {
      if (amountBids.length === 1) {
        // Only one bid at this amount
        result.push(amountBids[0]!);
      } else {
        // Multiple bids at same amount - pick the one with highest maxBidAmount
        const bestBid = amountBids.reduce((best, current) => {
          const currentMaxAmount = Number(current.maxBidAmount || 0);
          const bestMaxAmount = Number(best.maxBidAmount || 0);
          return currentMaxAmount > bestMaxAmount ? current : best;
        });
        result.push(bestBid);
      }
    });
  });

  // Step 4: Post-process the finalized list with added/updatedMaxBidAmount flagging and sorting
  return result
    .map((bid) => {
      // Find all USER-placed bids from the original list for same user+amount
      // to determine if this is a max bid update (auto-bids don't count)
      const originalUserBidsForSameUserAmount = bids.filter(
        (originalBid) =>
          originalBid.propertySeekerId === bid.propertySeekerId &&
          originalBid.currentBidAmount.toString() ===
            bid.currentBidAmount.toString() &&
          originalBid.placedBy === "USER",
      );

      const hasMaxBidAmount = bid.maxBidAmount != null;

      if (originalUserBidsForSameUserAmount.length === 1) {
        // Only one USER bid at this amount - could be addedMaxBidAmount if has maxBidAmount
        return {
          ...bid,
          addedMaxBidAmount: false,
          updatedMaxBidAmount: false,
        };
      } else if (originalUserBidsForSameUserAmount.length > 1) {
        // Multiple USER bids at this amount - this indicates updating max bid
        // Find the earliest bid at this amount
        const earliestBid = originalUserBidsForSameUserAmount.sort(
          (a, b) => a.createdAt.getTime() - b.createdAt.getTime(),
        )[1]!;

        // This bid is "addedMaxBidAmount" if it's the earliest and has maxBidAmount
        // This bid is "updatedMaxBidAmount" if it's later than earliest and has maxBidAmount
        const isSecondBidAtPrice = bid.id === earliestBid.id;

        return {
          ...bid,
          addedMaxBidAmount: isSecondBidAtPrice && hasMaxBidAmount,
          updatedMaxBidAmount: !isSecondBidAtPrice && hasMaxBidAmount,
        };
      }

      // Fallback (shouldn't happen)
      return {
        ...bid,
        addedMaxBidAmount: false,
        updatedMaxBidAmount: false,
      };
    })
    .sort((a, b) => {
      // Sort by currentBidAmount descending (highest first)
      const amountDiff =
        Number(b.currentBidAmount) - Number(a.currentBidAmount);
      if (amountDiff !== 0) return amountDiff;

      const maxBidAmountDiff =
        Number(b.maxBidAmount ?? 0) - Number(a.maxBidAmount ?? 0);
      if (maxBidAmountDiff !== 0) return maxBidAmountDiff;

      // Then sort by createdAt descending (newest first)
      return b.createdAt.getTime() - a.createdAt.getTime();
    });
}
